import { useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { toast } from "sonner";
import * as Icons from "lucide-react";
import { Phone, Mail, MapPin, Clock, Menu, X, Instagram, Facebook, Linkedin, ArrowRight, Loader2, Award, Info } from "lucide-react";
import { ThemeSwitcher, useTheme } from "@/components/ThemeSwitcher";
import {
  LOGO_URL, BANNER_URL, OWNER_PHOTO, LOGO_BG, HERO_BG, PHONE_DISPLAY, PHONE_ALT, PHONE_TEL, EMAIL,
  SERVICES, WHY, HOURS, HOURS_NOTE, SERVICE_OPTIONS, DOC_CATEGORIES, OFFICIAL_ACTS, ADDONS, ADDON_DISCLAIMER,
} from "@/data";
import { Accordion, AccordionItem, AccordionTrigger, AccordionContent } from "@/components/ui/accordion";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Section = ({ children, className = "", id }) => (
  <section id={id} className={`w-full px-6 md:px-10 ${className}`}>
    <div className="max-w-6xl mx-auto">{children}</div>
  </section>
);

const reveal = {
  hidden: { opacity: 0, y: 28 },
  show: (i = 0) => ({ opacity: 1, y: 0, transition: { duration: 0.6, delay: i * 0.08 } }),
};

const Header = ({ theme, setTheme }) => {
  const [open, setOpen] = useState(false);
  const links = [
    ["Services", "services"],
    ["Documents", "documents"],
    ["Why ADK Notary", "why"],
    ["Schedule", "schedule"],
  ];
  const go = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setOpen(false);
  };
  return (
    <header
      className="sticky top-0 z-50 backdrop-blur-xl"
      style={{ background: "color-mix(in srgb, var(--bg-background) 82%, transparent)", borderBottom: "1px solid var(--border-color)" }}
    >
      <div className="max-w-6xl mx-auto px-6 md:px-10 h-16 flex items-center justify-between gap-4">
        <button data-testid="brand-logo" onClick={() => go("hero")} className="flex items-center gap-3">
          <img src={LOGO_URL} alt="ADK Notary" className="h-10 w-10 rounded-full object-cover scale-[1.38]" style={{ background: LOGO_BG, border: "1px solid var(--border-color)" }} />
          <span className="font-heading text-lg font-bold tracking-wide">ADK NOTARY</span>
        </button>

        <nav className="hidden md:flex items-center gap-7">
          {links.map(([label, id]) => (
            <button key={id} data-testid={`nav-${id}`} onClick={() => go(id)}
              className="text-sm font-medium hover:opacity-70 transition-opacity" style={{ color: "var(--text-secondary)" }}>
              {label}
            </button>
          ))}
        </nav>

        <div className="flex items-center gap-3">
          <ThemeSwitcher theme={theme} setTheme={setTheme} />
          <a data-testid="header-call-btn" href={`tel:${PHONE_TEL}`}
            className="hidden sm:flex btn-accent items-center gap-2 px-4 py-2 text-sm font-semibold">
            <Phone size={15} strokeWidth={2} /> Call
          </a>
          <button className="md:hidden" onClick={() => setOpen(!open)} data-testid="mobile-menu-toggle" aria-label="Menu">
            {open ? <X size={22} /> : <Menu size={22} />}
          </button>
        </div>
      </div>

      {open && (
        <div className="md:hidden px-6 pb-4 flex flex-col gap-3" style={{ borderTop: "1px solid var(--border-color)" }}>
          {links.map(([label, id]) => (
            <button key={id} onClick={() => go(id)} className="text-left py-2 text-sm" style={{ color: "var(--text-secondary)" }}>
              {label}
            </button>
          ))}
        </div>
      )}
    </header>
  );
};

const Hero = () => (
  <div id="hero" className="relative overflow-hidden">
    <div className="absolute inset-0" style={{ backgroundImage: `url(${HERO_BG})`, backgroundSize: "cover", backgroundPosition: "center" }} />
    <div className="absolute inset-0" style={{ background: "var(--hero-overlay)" }} />
    <Section className="relative py-24 md:py-36">
      <div className="max-w-3xl">
        <motion.img
          initial={{ opacity: 0, scale: 0.9 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 0.7 }}
          src={LOGO_URL} alt="ADK Holdings" className="h-24 w-24 md:h-28 md:w-28 rounded-full object-cover scale-[1.38] mb-8"
          style={{ background: LOGO_BG, border: "2px solid var(--accent-primary)" }}
        />
        <motion.p variants={reveal} initial="hidden" animate="show" custom={1}
          className="uppercase tracking-[0.25em] text-xs md:text-sm font-semibold mb-4" style={{ color: "var(--accent-primary)" }}>
          Professional • Reliable • Mobile Notary
        </motion.p>
        <motion.h1 variants={reveal} initial="hidden" animate="show" custom={2}
          className="font-heading text-4xl sm:text-5xl lg:text-6xl font-bold leading-[1.05] mb-6">
          Convenient Notary Services, Delivered to Your Door
        </motion.h1>
        <motion.p variants={reveal} initial="hidden" animate="show" custom={3}
          className="text-base md:text-lg max-w-2xl mb-9" style={{ color: "var(--text-secondary)" }}>
          Whether you need a single signature verified or a complex set of loan documents executed,
          ADK Notary provides accurate, punctual, and legally binding notary services tailored to your schedule.
          We come to your home, office, or a convenient public location across South Florida.
        </motion.p>
        <motion.div variants={reveal} initial="hidden" animate="show" custom={4} className="flex flex-wrap gap-4">
          <button data-testid="hero-schedule-btn"
            onClick={() => document.getElementById("schedule")?.scrollIntoView({ behavior: "smooth" })}
            className="btn-accent px-7 py-3.5 font-semibold flex items-center gap-2">
            Schedule an Appointment <ArrowRight size={18} />
          </button>
          <a data-testid="hero-call-btn" href={`tel:${PHONE_TEL}`} className="btn-outline px-7 py-3.5 font-semibold flex items-center gap-2">
            <Phone size={18} strokeWidth={2} /> {PHONE_DISPLAY}
          </a>
        </motion.div>
      </div>
    </Section>
  </div>
);

const Services = () => (
  <Section id="services" className="py-20 md:py-28">
    <div className="mb-14 max-w-2xl">
      <p className="uppercase tracking-[0.2em] text-xs font-semibold mb-3" style={{ color: "var(--accent-primary)" }}>Our Core Services</p>
      <h2 className="font-heading text-3xl md:text-4xl font-bold">Everything you need, notarized correctly.</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
      {SERVICES.map((s, i) => {
        const Icon = Icons[s.icon] || Icons.FileText;
        const wide = i === 0 || i === 4;
        return (
          <motion.div key={s.title} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
            data-testid={`service-card-${i}`}
            className={`p-8 group ${wide ? "md:col-span-2" : ""}`}
            style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: "var(--radius-btn)" }}>
            <div className="inline-flex p-3 mb-5 rounded-full transition-transform group-hover:scale-110"
              style={{ background: "var(--bg-surface-2)", color: "var(--accent-primary)" }}>
              <Icon size={26} strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">{s.title}</h3>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{s.desc}</p>
          </motion.div>
        );
      })}
    </div>
  </Section>
);

const Why = () => (
  <Section id="why" className="py-20 md:py-28" >
    <div className="mb-14 text-center max-w-2xl mx-auto">
      <p className="uppercase tracking-[0.2em] text-xs font-semibold mb-3" style={{ color: "var(--accent-primary)" }}>Why Choose ADK Notary?</p>
      <h2 className="font-heading text-3xl md:text-4xl font-bold">Trusted by clients across South Florida.</h2>
    </div>
    <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
      {WHY.map((w, i) => {
        const Icon = Icons[w.icon] || Icons.Check;
        return (
          <motion.div key={w.title} variants={reveal} initial="hidden" whileInView="show" viewport={{ once: true }} custom={i}
            data-testid={`why-card-${i}`} className="text-center px-4">
            <div className="inline-flex p-4 mb-5 rounded-full" style={{ border: "1px solid var(--accent-primary)", color: "var(--accent-primary)" }}>
              <Icon size={30} strokeWidth={1.5} />
            </div>
            <h3 className="font-heading text-xl font-semibold mb-2">{w.title}</h3>
            <p className="text-sm leading-relaxed" style={{ color: "var(--text-secondary)" }}>{w.desc}</p>
          </motion.div>
        );
      })}
    </div>
  </Section>
);

const Documents = () => (
  <Section id="documents" className="py-20 md:py-28">
    <div className="mb-12 max-w-3xl">
      <p className="uppercase tracking-[0.2em] text-xs font-semibold mb-3" style={{ color: "var(--accent-primary)" }}>What We Can Notarize</p>
      <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Documents Florida Notaries Can Legally Notarize</h2>
      <p className="text-base" style={{ color: "var(--text-secondary)" }}>
        These categories come directly from Florida notary regulations and mobile-notary industry sources.
        Tap a category to see what's included.
      </p>
    </div>

    <div className="grid grid-cols-1 md:grid-cols-2 gap-x-8">
      {DOC_CATEGORIES.map((cat, i) => {
        const Icon = Icons[cat.icon] || Icons.FileText;
        return (
          <Accordion key={cat.title} type="single" collapsible data-testid={`doc-category-${i}`}>
            <AccordionItem value={cat.title} style={{ borderColor: "var(--border-color)" }}>
              <AccordionTrigger className="text-base font-heading font-semibold hover:no-underline py-5">
                <span className="flex items-center gap-3">
                  <span className="inline-flex p-2 rounded-full" style={{ background: "var(--bg-surface)", color: "var(--accent-primary)" }}>
                    <Icon size={18} strokeWidth={1.5} />
                  </span>
                  {cat.title}
                </span>
              </AccordionTrigger>
              <AccordionContent>
                <ul className="space-y-2.5 pb-4 pl-1">
                  {cat.items.map((it) => (
                    <li key={it} className="flex items-start gap-2.5 text-sm" style={{ color: "var(--text-secondary)" }}>
                      <Icons.Check size={16} className="mt-0.5 shrink-0" style={{ color: "var(--accent-primary)" }} />
                      <span>{it}</span>
                    </li>
                  ))}
                </ul>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        );
      })}
    </div>
  </Section>
);

const OfficialActs = () => (
  <Section id="official-acts" className="py-16">
    <div className="p-8 md:p-12" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: "var(--radius-btn)" }}>
      <div className="mb-10 text-center max-w-2xl mx-auto">
        <p className="uppercase tracking-[0.2em] text-xs font-semibold mb-3" style={{ color: "var(--accent-primary)" }}>Notarial Acts You Can Advertise</p>
        <h2 className="font-heading text-2xl md:text-3xl font-bold">Florida recognizes these official acts.</h2>
      </div>
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
        {OFFICIAL_ACTS.map((a, i) => {
          const Icon = Icons[a.icon] || Icons.Check;
          return (
            <div key={a.title} data-testid={`official-act-${i}`} className="text-center">
              <div className="inline-flex p-3.5 mb-4 rounded-full" style={{ border: "1px solid var(--accent-primary)", color: "var(--accent-primary)" }}>
                <Icon size={24} strokeWidth={1.5} />
              </div>
              <h3 className="font-heading text-base font-semibold mb-1.5">{a.title}</h3>
              <p className="text-xs leading-relaxed" style={{ color: "var(--text-secondary)" }}>{a.desc}</p>
            </div>
          );
        })}
      </div>
    </div>
  </Section>
);

const AddOns = () => (
  <Section id="add-ons" className="py-16 md:py-20">
    <div className="mb-10 max-w-2xl">
      <p className="uppercase tracking-[0.2em] text-xs font-semibold mb-3 flex items-center gap-2" style={{ color: "var(--accent-primary)" }}>
        <Award size={15} /> Mobile Notary Premium Add-Ons
      </p>
      <h2 className="font-heading text-3xl md:text-4xl font-bold mb-4">Gold or silver embossing for a distinguished finish.</h2>
    </div>
    <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5 mb-8">
      {ADDONS.map((a, i) => (
        <div key={a.title} data-testid={`addon-${i}`} className="p-6 flex flex-col gap-2"
          style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: "var(--radius-btn)" }}>
          <Award size={22} style={{ color: "var(--accent-primary)" }} />
          <h3 className="font-heading text-lg font-semibold">{a.title}</h3>
          <p className="text-sm" style={{ color: "var(--text-secondary)" }}>{a.desc}</p>
        </div>
      ))}
    </div>
    <div className="flex items-start gap-3 p-5 text-sm" style={{ background: "var(--bg-surface-2)", borderRadius: "var(--radius-btn)", color: "var(--text-secondary)" }} data-testid="addon-disclaimer">
      <Info size={18} className="mt-0.5 shrink-0" style={{ color: "var(--accent-primary)" }} />
      <p>{ADDON_DISCLAIMER}</p>
    </div>
  </Section>
);

const Schedule = () => {
  const empty = { name: "", phone: "", email: "", preferred_date: "", service_type: SERVICE_OPTIONS[0], documents: "", message: "" };
  const [form, setForm] = useState(empty);
  const [loading, setLoading] = useState(false);
  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.phone || !form.email || !form.preferred_date) {
      toast.error("Please fill in your name, phone, email and preferred date.");
      return;
    }
    setLoading(true);
    try {
      await axios.post(`${API}/appointments`, form);
      toast.success("Request sent! We'll reach out shortly to confirm.");
      setForm(empty);
    } catch (err) {
      toast.error("Something went wrong. Please call us at " + PHONE_DISPLAY);
    } finally {
      setLoading(false);
    }
  };

  return (
    <Section id="schedule" className="py-20 md:py-28">
      <div className="grid grid-cols-1 lg:grid-cols-5 gap-10">
        <div className="lg:col-span-3">
          <p className="uppercase tracking-[0.2em] text-xs font-semibold mb-3" style={{ color: "var(--accent-primary)" }}>Book Now</p>
          <h2 className="font-heading text-3xl md:text-4xl font-bold mb-8">Schedule an Appointment</h2>
          <form onSubmit={submit} className="space-y-4" data-testid="appointment-form">
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="input-name" className="field px-4 py-3 w-full" placeholder="Full Name *" value={form.name} onChange={set("name")} />
              <input data-testid="input-phone" className="field px-4 py-3 w-full" placeholder="Phone *" value={form.phone} onChange={set("phone")} />
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              <input data-testid="input-email" type="email" className="field px-4 py-3 w-full" placeholder="Email *" value={form.email} onChange={set("email")} />
              <input data-testid="input-date" type="date" className="field px-4 py-3 w-full" value={form.preferred_date} onChange={set("preferred_date")} />
            </div>
            <select data-testid="input-service" className="field px-4 py-3 w-full" value={form.service_type} onChange={set("service_type")}>
              {SERVICE_OPTIONS.map((o) => <option key={o} value={o}>{o}</option>)}
            </select>
            <textarea data-testid="input-documents" className="field px-4 py-3 w-full" rows={3}
              placeholder="Type of documents to be notarized" value={form.documents} onChange={set("documents")} />
            <textarea data-testid="input-message" className="field px-4 py-3 w-full" rows={2}
              placeholder="Anything else we should know? (address, timing, etc.)" value={form.message} onChange={set("message")} />
            <div className="flex flex-wrap gap-4 pt-2">
              <button data-testid="submit-appointment-btn" type="submit" disabled={loading}
                className="btn-accent px-7 py-3.5 font-semibold flex items-center gap-2 disabled:opacity-60">
                {loading ? <><Loader2 size={18} className="animate-spin" /> Sending…</> : <>Request Appointment <ArrowRight size={18} /></>}
              </button>
              <a data-testid="form-call-btn" href={`tel:${PHONE_TEL}`} className="btn-outline px-7 py-3.5 font-semibold flex items-center gap-2">
                <Phone size={18} strokeWidth={2} /> Call Instead
              </a>
            </div>
          </form>
        </div>

        <div className="lg:col-span-2 p-8 space-y-8" style={{ background: "var(--bg-surface)", border: "1px solid var(--border-color)", borderRadius: "var(--radius-btn)" }}>
          <div>
            <h3 className="font-heading text-xl font-semibold mb-4 flex items-center gap-2"><Phone size={18} style={{ color: "var(--accent-primary)" }} /> Contact</h3>
            <a href={`tel:${PHONE_TEL}`} data-testid="contact-phone" className="block text-lg font-semibold hover:opacity-70">{PHONE_DISPLAY}</a>
            <p className="text-sm mb-3" style={{ color: "var(--text-secondary)" }}>{PHONE_ALT}</p>
            <a href={`mailto:${EMAIL}`} data-testid="contact-email" className="flex items-center gap-2 text-sm hover:opacity-70" style={{ color: "var(--text-secondary)" }}>
              <Mail size={15} /> {EMAIL}
            </a>
          </div>
          <div>
            <h3 className="font-heading text-xl font-semibold mb-3 flex items-center gap-2"><MapPin size={18} style={{ color: "var(--accent-primary)" }} /> Service Area</h3>
            <p className="text-sm" style={{ color: "var(--text-secondary)" }}>Mobile only — we come to you across South Florida. Home, office, or a convenient public location.</p>
          </div>
          <div>
            <h3 className="font-heading text-xl font-semibold mb-3 flex items-center gap-2"><Clock size={18} style={{ color: "var(--accent-primary)" }} /> Hours</h3>
            <ul className="space-y-1.5 text-sm">
              {HOURS.map((h) => (
                <li key={h.day} className="flex justify-between gap-4">
                  <span style={{ color: "var(--text-secondary)" }}>{h.day}</span>
                  <span className="font-medium text-right">{h.time}</span>
                </li>
              ))}
            </ul>
            <p className="text-xs mt-3 italic" style={{ color: "var(--accent-primary)" }} data-testid="hours-note">{HOURS_NOTE}</p>
          </div>
        </div>
      </div>
    </Section>
  );
};

const HoursBanner = () => (
  <Section id="hours" className="py-6">
    <div className="p-6 md:p-8 flex flex-col md:flex-row items-center justify-between gap-4 text-center md:text-left"
      style={{ background: "var(--accent-primary)", color: "var(--accent-contrast)", borderRadius: "var(--radius-btn)" }}>
      <div>
        <p className="font-heading text-2xl font-bold">Weekends. Evenings. We Come to You.</p>
        <p className="text-sm opacity-90">Mobile notary serving South Florida — text or call for a quote.</p>
      </div>
      <a href={`tel:${PHONE_TEL}`} data-testid="banner-call-btn"
        className="px-6 py-3 font-semibold inline-flex items-center gap-2"
        style={{ background: "var(--accent-contrast)", color: "var(--accent-primary)", borderRadius: "var(--radius-btn)" }}>
        <Phone size={18} /> Text or Call for a Quote
      </a>
    </div>
  </Section>
);

const Footer = () => (
  <footer className="py-20 mt-6" style={{ borderTop: "1px solid var(--border-color)", background: "var(--bg-surface)" }}>
    <div className="max-w-6xl mx-auto px-6 flex flex-col items-center text-center gap-6">
      <div className="flex items-center gap-5">
        {[["Instagram", Instagram], ["Facebook", Facebook], ["Linkedin", Linkedin]].map(([name, Icon]) => (
          <a key={name} href="#" data-testid={`social-${name.toLowerCase()}`} aria-label={name}
            className="p-2.5 rounded-full transition-transform hover:scale-110"
            style={{ border: "1px solid var(--border-color)", color: "var(--text-secondary)" }}>
            <Icon size={18} strokeWidth={1.5} />
          </a>
        ))}
      </div>
      <p className="text-sm" style={{ color: "var(--text-secondary)" }}>
        {PHONE_DISPLAY} · {PHONE_ALT} · <a href={`mailto:${EMAIL}`} className="hover:opacity-70">{EMAIL}</a>
      </p>
      <img src={LOGO_URL} alt="ADK Notary" className="h-28 w-28 rounded-full object-cover scale-[1.38]" style={{ background: LOGO_BG, border: "2px solid var(--accent-primary)" }} />
      <img src={BANNER_URL} alt="ADK Notary" className="h-16 md:h-20 rounded-lg object-contain px-2" style={{ background: LOGO_BG }} />
      <div className="flex flex-col items-center gap-3 mt-2" data-testid="owner-profile">
        <img src={OWNER_PHOTO} alt="Tahara Orange" className="h-32 w-32 rounded-full object-cover" style={{ border: "3px solid var(--accent-primary)" }} />
        <div>
          <p className="font-heading text-xl font-bold tracking-wide">Tahara Orange</p>
          <p className="text-sm" style={{ color: "var(--accent-primary)" }}>Owner &amp; Certified Mobile Notary</p>
        </div>
      </div>
      <p className="text-xs" style={{ color: "var(--text-secondary)" }}>© {new Date().getFullYear()} ADK Notary. Mobile Notary Services — South Florida. All rights reserved.</p>
    </div>
  </footer>
);

export default function Landing() {
  const { theme, setTheme } = useTheme();
  return (
    <div style={{ background: "var(--bg-background)", color: "var(--text-primary)" }}>
      <Header theme={theme} setTheme={setTheme} />
      <Hero />
      <Services />
      <Documents />
      <OfficialActs />
      <AddOns />
      <Why />
      <HoursBanner />
      <Schedule />
      <Footer />
    </div>
  );
}
